import { useId, type ReactElement } from 'react';
import { Dialog, Heading, Modal, ModalOverlay } from 'react-aria-components';
import { Button } from '../../ui/Button';
import { Icon, type IconName } from '../../ui/Icon';
import s from './VoiceIntro.module.css';

// APP-104 §3.2a (Tom, 2026-10-22): the first time a click on Feedback would start recording, this
// says what's about to happen and how to give good feedback; recording starts only at Start
// recording. Every word of copy here is fixed. It carries `data-feedback-ui` like the other
// feedback overlays.

export const INTRO_TITLE = 'Send feedback by voice';
export const INTRO_LEAD =
  'When you select Start recording, MyRewards listens and writes down what you say.';

const POINTS: readonly { icon: IconName; words: string }[] = [
  {
    icon: 'message',
    words:
      'Speak naturally. Tell us what on this page should change or could work better, or what happened that you didn’t expect.',
  },
  {
    icon: 'check',
    words:
      'Select Done when you’ve finished. You’ll see your words and can change them before you send.',
  },
  { icon: 'keyboard', words: 'Rather type? Select Type instead, here or while you’re recording.' },
];

export const INTRO_NOTE =
  'Your browser may ask to use your microphone. Choose Allow. Only your words are kept, never the recording. A screenshot of this page goes with your feedback, and you can leave it out before you send.';

export interface VoiceIntroProps {
  /** Start recording: the recording bar takes over. */
  onStart: () => void;
  /** Type instead: the "Send feedback" dialog opens for typing. */
  onType: () => void;
  /** Not now, or Escape: nothing is started. */
  onCancel: () => void;
}

export function VoiceIntro({ onStart, onType, onCancel }: VoiceIntroProps): ReactElement {
  const leadId = useId();
  return (
    <ModalOverlay
      isOpen
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
      // A stray click outside doesn't close it; Escape and Not now do.
      isDismissable={false}
      className={s.overlay}
      data-feedback-ui
    >
      <Modal className={s.modal}>
        <Dialog className={s.dialog} aria-describedby={leadId}>
          <div className={s.head}>
            <span className={s.badge} aria-hidden="true">
              <Icon name="mic" size={22} />
            </span>
            <Heading slot="title" className={`disp ${s.title}`}>
              {INTRO_TITLE}
            </Heading>
          </div>
          <p id={leadId} className={s.lead}>
            {INTRO_LEAD}
          </p>
          <ul className={s.points}>
            {POINTS.map((point) => (
              <li key={point.icon} className={s.point}>
                <Icon name={point.icon} size={18} className={s.pointIcon} />
                <span>{point.words}</span>
              </li>
            ))}
          </ul>
          <p className={s.note}>{INTRO_NOTE}</p>
          <div className={s.actions}>
            <Button variant="ghost" onPress={onCancel}>
              Not now
            </Button>
            <Button variant="ghost" onPress={onType} icon={<Icon name="keyboard" size={16} />}>
              Type instead
            </Button>
            <Button
              variant="navy"
              onPress={onStart}
              icon={<Icon name="mic" size={16} />}
              // eslint-disable-next-line jsx-a11y/no-autofocus -- the dialog's main answer, so Enter starts
              autoFocus
            >
              Start recording
            </Button>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
